import { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { useJsApiLoader } from "@react-google-maps/api";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import {
  useGetAddressByUserId,
  useSaveAddress,
  useActualizarDireccionActiva,
  useEliminarDireccion, 
} from "@/hooks/useHome";
import { useAuth } from "@/_zustand/auth";
import Trash from "@/assets/imgs/trash_icon.png";
import { truncateText } from "../../lib";
import "./MiCasaModal.css"; 

const GOOGLE_LIBRARIES = ["places"];

export const ModalMiCasa = ({ show, handleClose, onSelect }) => {
  // Instancias de los hooks de mutación
  const getAddressById = useGetAddressByUserId();
  const { mutateAsync: getAddressByIdAsync } = getAddressById;
  const actualizarDireccionActiva = useActualizarDireccionActiva(); // Renombrado para claridad
  const eliminarDireccion = useEliminarDireccion();
  const saveAddress = useSaveAddress();

  const { user, setUserAddress, setUserAddressName, setUserAddressTag } = useAuth();

  // Estado local para las direcciones
  const [direcciones, setDirecciones] = useState([]); // Inicializado como array vacío, se llenará con la API
  const [selectedId, setSelectedId] = useState(null);
  const [direccionSeleccionada, setDireccionSeleccionada] = useState("");
  const [alias, setAlias] = useState("");

  const [address, setAddress] = useState("");

  // Hook para cargar Google Maps API
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_APIKEY_GOOGLE,
    libraries: GOOGLE_LIBRARIES,
  });

  // Efecto para cargar direcciones al abrir el modal
  useEffect(() => {
    if (show && user?.usuario_id) {
      getAddressByIdAsync(user.usuario_id)
        .then((resp) => {
          if (resp.length > 0) {
            const activeAddress = resp.find((addr) => addr.active);
            if(activeAddress){
              setUserAddress([activeAddress.latitud, activeAddress.longitud]);
              setUserAddressTag(activeAddress.alias);
              setDirecciones(resp || []);
            }
            else{
              setUserAddress([resp[0].latitud, resp[0].longitud]);
              setUserAddressTag(resp[0].alias);
              setDirecciones(resp || []);
            }
          }
        })
        .catch((error) => {
          console.error("Error al obtener direcciones:", error);
          toast.error("Error al cargar tus direcciones.");
          setDirecciones([]); // Limpia las direcciones en caso de error
        });
    } else if (!show) {
      setDirecciones([]);
      setSelectedId(null);
      setDireccionSeleccionada("");
    }
  }, [show, user?.usuario_id, getAddressByIdAsync, setUserAddress, setUserAddressTag]);

  useEffect(() => {
    if (user?.usuario_id) {
      getAddressByIdAsync(user.usuario_id)
        .then((resp) => {
          if (resp.length > 0) {
            const activeAddress = resp.find((addr) => addr.active);
            if(activeAddress){
              setUserAddress([activeAddress.latitud, activeAddress.longitud]);
              setUserAddressName(activeAddress.direccion);
              setUserAddressTag(activeAddress.alias);
              setDirecciones(resp || []);
            }
            else{
              setUserAddress([resp[0].latitud, resp[0].longitud]);
              setUserAddressName(resp[0].direccion);
              setUserAddressTag(resp[0].alias);
              setDirecciones(resp || []);
            }
          }
        })
        .catch((error) => {
          console.error("Error al obtener direcciones:", error);
          toast.error("Error al cargar tus direcciones.");
          setDirecciones([]); // Limpia las direcciones en caso de error
        });
    } else if (!show) {
      setDirecciones([]);
      setSelectedId(null);
      setDireccionSeleccionada("");
    }
  }, [user, show, getAddressByIdAsync, setUserAddress, setUserAddressName, setUserAddressTag]);

  useEffect(() => {
    const activeAddress = direcciones.find((addr) => addr.active);
    if (activeAddress) {
      setSelectedId(activeAddress.id);
    }
  }, [direcciones]);

  const handleSelect = async (selectedAddress) => {
    setAddress(selectedAddress); // Actualiza el input con la dirección seleccionada
    setDireccionSeleccionada(selectedAddress);
   
  };

  // Esta función es para manejar el cambio en el input
  const handleChange = (newAddress) => {
    setAddress(newAddress);
  };

  const handleRadioChange = async (id) => {
    setSelectedId(id);
    const selected = direcciones.find((a) => a.id === id);

    if (selected) {
      onSelect(selected.direccion);
      setUserAddressName(selected.direccion);
      setUserAddressTag(selected.alias);
      try {
        let dataSend = {
          usuario_id: user.usuario_id,
          id: id,
        };

        // Usamos mutateAsync y then/catch para esperar el resultado
        await actualizarDireccionActiva.mutateAsync(dataSend);
        toast.success("Dirección activa actualizada exitosamente.");
        handleClose(); // Cerrar el modal solo después de la operación exitosa
      } catch (error) {
        console.error("Error actualizando dirección activa:", error);
        toast.error("Ha ocurrido un error al actualizar la dirección activa.");
      }
    }
  };

  const handleEliminar = async (direccionToDelete) => {
    // Hacemos la función asíncrona
    

    const esActiva = direccionToDelete.active;
    const otrasDirecciones = direcciones.filter(
      (d) => d.id !== direccionToDelete.id
    );

    try {
      // 1. Si la dirección a eliminar está activa Y hay otras direcciones disponibles
      if (esActiva && otrasDirecciones.length > 0) {
        
        // 2. Activar la primera disponible
        const nuevaActiva = otrasDirecciones[0];
        let dataSendActiva = {
          usuario_id: user.usuario_id,
          id: nuevaActiva.id,
        };
        // Esperamos a que la actualización de la dirección activa se complete
        await actualizarDireccionActiva.mutateAsync(dataSendActiva);
        toast("Se ha establecido una nueva dirección activa.", { icon: "ℹ️" }); // Notificación informativa
      } else if (esActiva && otrasDirecciones.length === 0) {
        // Caso especial: la última dirección activa se intenta eliminar
        toast.error(
          "No puedes eliminar tu única dirección activa. Agrega una nueva primero."
        );
        return; // Detenemos la ejecución aquí
      }

      // 3. Eliminar la dirección
     
      await eliminarDireccion.mutateAsync(direccionToDelete.id); // Pasa solo el ID si el servicio lo espera

      // Si la eliminación fue exitosa, entonces mostramos el toast y cerramos el modal
      toast.success("Se ha eliminado exitosamente la dirección.");
      // Actualiza el estado local de direcciones después de la eliminación exitosa
      setDirecciones(otrasDirecciones);
      handleClose();
    } catch (error) {
      console.error(
        "-> handleEliminar: Error al eliminar o actualizar dirección:",
        error
      );
      // Los toasts de error se manejan en el onError del hook useEliminarDireccion,
      // aquí solo logueamos o hacemos algo adicional si es necesario.
      toast.error("Ha ocurrido un error al eliminar la dirección."); // Fallback toast
    }
  };

  const handleGuardar = async () => {
    try {
      const results = await geocodeByAddress(address);
      const latLng = await getLatLng(results[0]);
      setDireccionSeleccionada(address);

      let dataSend = {
        usuario_id: user.usuario_id,
        direccion: direccionSeleccionada,
        latitud: latLng.lat,
        longitud: latLng.lng,
        alias: alias || "Casa", // Se asigna "Casa" por defecto si alias está vacío
        isDefault: true, // al guardar una nueva, se guarda activa sea por defecto
      };

      setUserAddress([dataSend.latitud, dataSend.longitud]);
      

      await saveAddress.mutateAsync(dataSend);
      toast.success("Se ha guardado su dirección exitosamente.");
      onSelect(direccionSeleccionada);
      setUserAddressName(dataSend.direccion); // Usa la dirección del objeto dataSend
      setUserAddressTag(alias);
      // Actualiza el estado local para reflejar la nueva dirección
      // Tendrás que recargar las direcciones si tu API no devuelve la lista completa
      // o invalidar la query de getAddressByUserId para que se refetch
      getAddressByIdAsync(user.usuario_id) // Refresca la lista después de guardar
        .then((updatedResp) => setDirecciones(updatedResp || []))
        .catch((err) => console.error("Error refrescando direcciones:", err));

      // Limpiar y cerrar
      setAlias("");
      setDireccionSeleccionada("");
      setAddress("");
      handleClose();
    } catch (error) {
      console.error("-> handleGuardar: Error al guardar dirección:", error);
      toast.error("Ha ocurrido un error al guardar su dirección.");
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="xl">
      <div className="custom-modal">
        {/* Encabezado */}
        <div className="modal-header border-0">
          <h5 className="modal-title-address w-100 text-center">Direcciones</h5>
          <button type="button" className="close" onClick={handleClose}>
            &times;
          </button>
        </div>

        {/* Contenido */}
        <div className="modal-body-address">
          {/* Input nueva dirección */}
          <div className="mb-4 d-flex align-items-center address-input">
            {address.length == 0 && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-search"
                viewBox="0 1 18 16"
              >
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0" />
              </svg>
            )}
            {isLoaded && (
              <PlacesAutocomplete
                value={address}
                onChange={handleChange}
                onSelect={handleSelect}
                // Opciones similares a las que ya usabas
                searchOptions={{
                  componentRestrictions: { country: "cl" },
                  types: ["address"],
                }}
              >
                {({
                  getInputProps,
                  suggestions,
                  getSuggestionItemProps,
                  loading,
                }) => (
                  <div className="autocomplete-container">
                    <input
                      {...getInputProps({
                        placeholder: "Agrega una nueva dirección...",
                        className: "form-control location-search-input",
                        style: { border: "none" }, // Tu estilo original
                      })}
                    />
                    <div className="autocomplete-dropdown-container">
                      {loading && <div>Cargando sugerencias...</div>}
                      {suggestions.map((suggestion) => {
                        const className = suggestion.active
                          ? "suggestion-item--active"
                          : "suggestion-item";
                        // `style` para resaltar la sugerencia activa
                        const style = suggestion.active
                          ? {
                              backgroundColor: "#fafafa",
                              cursor: "pointer",
                              color: "#656464",
                              paddingLeft: "0.9rem",
                            }
                          : {
                              backgroundColor: "#ffffff",
                              cursor: "pointer",
                              color: "#656464",
                              paddingLeft: "0.9rem",
                            };
                        return (
                          <div
                            {...getSuggestionItemProps(suggestion, {
                              className,
                              style,
                            })}
                            key={suggestion.placeId} // usa placeId para la key
                          >
                            <span>{suggestion.description}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </PlacesAutocomplete>
            )}
          </div>

          {/* Ubicación actual */}
          <div className="mb-4 d-flex align-items-center">
            <i className="bi bi-geo-alt-fill text-success me-2" />
            <div>
              <strong>Usar mi ubicación actual</strong>
              <p className="mb-0 text-muted small">
                Encuentra máquinas cerca de ti
              </p>
            </div>
          </div>

          <hr />

          {/* Inputs solo visibles si ya seleccionó dirección */}
          {direccionSeleccionada ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Alias</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ej: Casa, Trabajo..."
                  value={alias}
                  onChange={(e) => setAlias(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Dirección</Form.Label>
                <Form.Control
                  type="text"
                  readOnly
                  value={direccionSeleccionada}
                />
              </Form.Group>

              <Button
                variant="success"
                className="modal-address-button"
                onClick={handleGuardar}
                block
              >
                Guardar dirección
              </Button>
            </>
          ) : (
            <div>
              <div className="mb-4">
                <strong>Tus direcciones creadas</strong>
              </div>
              {direcciones.length === 0 && (
                <p className="text-muted text-center">
                  No tienes direcciones guardadas.
                </p>
              )}
              {direcciones.map((dir) => (
                <div
                  key={dir.id}
                  className="d-flex justify-content-between align-items-center mb-2"
                >
                  <div className="d-flex align-items-center">
                    {" "}
                    {/* Eliminado mb-4 duplicado */}
                    <input
                      className="form-check-input me-2"
                      type="radio"
                      name="direccion"
                      id={`addr-${dir.id}`}
                      checked={selectedId === dir.id}
                      onChange={() => handleRadioChange(dir.id)}
                    />
                    <span style={{ minWidth: "260px" }}>{truncateText(dir.direccion)}</span>
                    <span
                      className="badge ms-2"
                      style={{ backgroundColor: "#E6ECF1", width: "78px" }}
                    >
                      {truncateText(dir.alias)}
                    </span>
                    {/* El icono de trash está duplicado, elimina uno. Usaremos el del botón. */}
                  </div>
                  <button
                    className="btn btn-sm btn-link text-danger"
                    onClick={() => handleEliminar(dir)}
                    title="Eliminar dirección" // Añadido título para accesibilidad
                  >
                    <img
                      src={Trash}
                      alt="delete-address"
                      className="trash-card-logo"
                    />
                    {/* <i className="bi bi-trash3" /> Si usas Bootstrap Icons, este es el icono */}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
